import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ChangeDetectionStrategy,
  Input,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, map, switchMap, withLatestFrom } from 'rxjs';

import { IconLibrary } from '@finnairoyj/fcom-ui-styles';

import { AppState } from '@fcom/core';
import { CmsDataService } from '@fcom/core/services';
import { profile } from '@fcom/core/selectors';
import { CmsPicture, CmsTaxonomyPicture } from '@fcom/core-api';
import { ProfileTier } from '@fcom/core-api/login';
import { TIER_DATA_MAP, LoyaltyTierService, MemberTierDetails, type TierDataMap } from '@fcom/common';
import { AspectRatios } from '@fcom/ui-components';
import { finShare } from '@fcom/rx';
import { LanguageService } from '@fcom/ui-translate';

import { formatMemberNumber } from '../../utils/member.utils';

export type PlusCardData = Pick<
  MemberTierDetails,
  'cardTier' | 'cardLifetimeTierFlag' | 'cardTierFullName' | 'isStartingTier' | 'cardExpirationDate'
> & {
  memberNumberFormatted: string;
  firstName: string;
  lastName: string;
};

@Component({
  selector: 'fcom-plus-card',
  templateUrl: 'plus-card.component.html',
  styleUrls: ['./plus-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PlusCardComponent implements OnInit, OnDestroy {
  private store$ = inject<Store<AppState>>(Store);
  private host = inject(ElementRef);
  private loyaltyTierService = inject(LoyaltyTierService);
  private cmsDataService = inject(CmsDataService);
  private platform = inject(PLATFORM_ID);
  private languageService = inject(LanguageService);

  readonly AspectRatios = AspectRatios;
  readonly IconLibrary = IconLibrary;
  readonly ProfileTier = ProfileTier;

  readonly BASE_WIDTH = 327;
  readonly BASE_FONT_SIZE = 16;
  readonly TIER_DATA_MAP: TierDataMap = TIER_DATA_MAP;

  aviosLogo$: Observable<CmsPicture>;
  oneworldLogo$: Observable<CmsPicture>;
  plusCardData$: Observable<PlusCardData>;

  resizeObserver: ResizeObserver;
  fontSize$ = new BehaviorSubject(this.BASE_FONT_SIZE);

  @ViewChild('plusCard') plusCardElementRef: ElementRef;

  @Input()
  showCardExpirationDateIfApplicable = true;

  ngOnInit(): void {
    const profile$ = this.store$.pipe(profile(), finShare());
    const logos$: Observable<Record<string, CmsPicture>> = profile$.pipe(
      switchMap((memberProfile) => {
        const aviosLogoName =
          memberProfile.cardTier === ProfileTier.BASIC || memberProfile.cardTier === ProfileTier.JUNIOR
            ? CmsTaxonomyPicture.FINNAIR_AVIOS_KEYLINE_NAVY
            : CmsTaxonomyPicture.FINNAIR_AVIOS_KEYLINE_WHITE;
        const taxonomyTags = [aviosLogoName, TIER_DATA_MAP[memberProfile.tier]?.oneworldLogo].filter(Boolean);

        return this.cmsDataService
          .getPicturesByTaxonomyTags(taxonomyTags, this.languageService.langValue, this.languageService.localeValue)
          .pipe(
            map((p) => {
              return { ...p, aviosLogoName: p[aviosLogoName] };
            })
          );
      }),
      finShare()
    );
    this.aviosLogo$ = logos$.pipe(map((logos) => logos?.aviosLogoName));
    this.oneworldLogo$ = logos$.pipe(
      withLatestFrom(profile$),
      map(([logos, memberProfile]) => logos[TIER_DATA_MAP[memberProfile.tier]?.oneworldLogo])
    );

    this.plusCardData$ = this.loyaltyTierService.getMemberTierDetails().pipe(
      withLatestFrom(profile$),
      map(
        ([
          { cardTier, cardTierFullName, isStartingTier, cardLifetimeTierFlag, cardExpirationDate },
          { memberNumber, firstname, lastname },
        ]) => ({
          cardTier,
          cardTierFullName,
          isStartingTier,
          cardLifetimeTierFlag,
          cardExpirationDate,
          memberNumberFormatted: formatMemberNumber(memberNumber),
          firstName: firstname,
          lastName: lastname,
        })
      )
    );

    if (isPlatformBrowser(this.platform)) {
      this.resizeObserver = new ResizeObserver((entries) => {
        this.fontSize$.next((this.BASE_FONT_SIZE * entries[0].contentRect.width) / this.BASE_WIDTH);
      });

      this.resizeObserver.observe(this.host.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver && this.resizeObserver.disconnect();
  }
}
